import React, { useEffect, useState } from 'react';
import { Zap, Building2, Copy, Check, Clock } from 'lucide-react';
import { adminApi } from '../../lib/api';

type AutomationRequest = {
    id: string;
    client_id: string;
    company_name?: string;
    platform: string;
    api_key: string;
    status: 'pending' | 'traite';
    created_at: string;
};

const PLATFORM_LABELS: Record<string, string> = {
    zenchef: 'Zenchef',
    thefork: 'TheFork Manager',
    resengo: 'Resengo',
    autre: 'Autre',
};

const formatDate = (d?: string) => {
    if (!d) return '';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return d;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' à ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export const AdminExceptionServiceAutomationRequests: React.FC = () => {
    const [requests, setRequests] = useState<AutomationRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const load = async () => {
        try {
            const data = await adminApi.getExceptionServiceAutomationRequests();
            if (Array.isArray(data)) setRequests(data);
        } catch (e) {
            console.error('Failed to load exception service automation requests', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCopy = async (req: AutomationRequest) => {
        try {
            await navigator.clipboard.writeText(req.api_key);
            setCopiedId(req.id);
            setTimeout(() => setCopiedId(null), 1500);
        } catch (e) { console.error('Copy failed', e); }
    };

    const handleToggleStatus = async (req: AutomationRequest) => {
        setProcessingId(req.id);
        try {
            await adminApi.markExceptionServiceAutomationRequest(req.id, req.status === 'pending' ? 'traite' : 'pending');
            await load();
        } catch (e) {
            console.error('Failed to update request status', e);
        } finally {
            setProcessingId(null);
        }
    };

    const pending = requests.filter((r) => r.status === 'pending');
    const processed = requests.filter((r) => r.status !== 'pending');

    const renderCard = (req: AutomationRequest) => (
        <div key={req.id} className="bg-surface border border-border rounded-2xl p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-content">{PLATFORM_LABELS[req.platform] || req.platform}</span>
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${req.status === 'pending' ? 'bg-warning-500/10 text-warning-500 border-warning-500/20' : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'}`}>
                            {req.status === 'pending' ? 'En attente' : 'Traitée'}
                        </span>
                    </div>
                    <p className="text-xs text-content-muted flex items-center gap-1.5">
                        <Building2 size={12} /> {req.company_name || 'Client inconnu'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs bg-surface-soft border border-border rounded-lg px-2 py-1 text-content font-mono">{req.api_key}</code>
                        <button onClick={() => handleCopy(req)} className="text-content-muted hover:text-content transition-colors" title="Copier la clé API">
                            {copiedId === req.id ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                        </button>
                    </div>
                    <p className="text-[11px] text-content-muted">Soumise le {formatDate(req.created_at)}</p>
                </div>
                <button
                    onClick={() => handleToggleStatus(req)}
                    disabled={processingId === req.id}
                    className={`shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl transition-all border disabled:opacity-50 ${
                        req.status === 'pending'
                            ? 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border-emerald-500/20'
                            : 'bg-surface-soft hover:bg-surface text-content-muted border-border'
                    }`}
                >
                    {req.status === 'pending' ? 'Marquer comme traitée' : 'Remettre en attente'}
                </button>
            </div>
        </div>
    );

    return (
        <div className="space-y-8">
            <header className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
                    <Zap size={22} />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-content">Demandes d'automatisation</h1>
                    <p className="text-sm text-content-muted mt-1">Service d'exception — clés API transmises par les clients pour connecter leur plateforme de réservation.</p>
                </div>
            </header>

            {loading ? (
                <p className="text-content-muted text-sm">Chargement...</p>
            ) : (
                <>
                    <section className="space-y-4">
                        <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest">En attente ({pending.length})</h2>
                        {pending.length === 0 ? (
                            <div className="text-center py-12 border border-dashed border-border rounded-3xl bg-surface/40">
                                <Clock size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                                <p className="text-content-muted text-sm">Aucune demande en attente.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">{pending.map(renderCard)}</div>
                        )}
                    </section>

                    {processed.length > 0 && (
                        <section className="space-y-4">
                            <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest">Traitées</h2>
                            <div className="space-y-3">{processed.map(renderCard)}</div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
};
